/**
 * HouseholdDashboard — "Your kitchen at a glance".
 *
 * Route: /food-pulse/household
 *
 * Pulls /api/household/summary and renders four tiles:
 *   • Spend this month (with previous-month delta)
 *   • Spend split by vertical (grocery/dining/cafe/retail/etc.)
 *   • Top stores & top categories
 *   • Expiring within 3 days from the pantry
 *
 * Also links contribution points + receipt history so the page is a
 * one-stop control room for the household food economy.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DollarSign, TrendingUp, TrendingDown, Store, Tag, Clock,
  Receipt, Award, ArrowRight, Camera, Loader2, MapPin, AlertTriangle,
  Utensils, Coffee, ShoppingBag, Pill, Fuel, Sprout,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";
const RED   = "#7B2A2E";

const VERTICAL_ICON = {
  grocery:    ShoppingBag,
  restaurant: Utensils,
  cafe:       Coffee,
  farmers:    Sprout,
  retail:     Tag,
  pharmacy:   Pill,
  fuel:       Fuel,
  other:      Receipt,
};

const CURRENCY = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function HouseholdDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    http.get("/household/summary")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
        <div className="max-w-md text-center">
          <Receipt className="w-14 h-14 mx-auto mb-4" style={{ color: AMBER }} />
          <h1 className="font-display text-3xl mb-2">Sign in to see your household dashboard</h1>
          <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }}>
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: GREEN }} />
    </div>
  );

  const empty = !data || (data.this_month?.receipt_count === 0 && data.pantry_total === 0);
  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="household-dashboard">
      <SeoHead section="food-pulse-household" title="Household dashboard — Food Pulse"
               description="Your grocery spend, dining out, pantry, contribution points — all in one place." />

      {/* Header ---------------------------------------------- */}
      <div className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>— Household</div>
            <h1 className="font-display text-4xl sm:text-5xl">Your kitchen at a glance</h1>
            <p className="opacity-70 mt-1 text-sm">
              Every receipt you snap fills these tiles. Grocery + farmers-market receipts also stock the pantry with estimated expiry dates.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/food-pulse/receipts/new"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-white flex items-center gap-2 shadow"
                  style={{ background: GREEN }}
                  data-testid="household-snap-receipt">
              <Camera className="w-4 h-4" /> Snap a receipt
            </Link>
            <Link to="/food-pulse/receipts/mine"
                  className="rounded-full px-4 py-2 text-sm font-semibold border-2 flex items-center gap-2"
                  style={{ borderColor: GREEN, color: GREEN, background: "white" }}>
              <Receipt className="w-4 h-4" /> Receipt history
            </Link>
          </div>
        </div>
      </div>

      {empty ? (
        <div className="max-w-2xl mx-auto px-4 pb-16">
          <div className="rounded-3xl border-2 border-dashed p-12 text-center"
               style={{ borderColor: AMBER, background: "#FFF7E4" }}
               data-testid="household-empty">
            <Receipt className="w-16 h-16 mx-auto opacity-40 mb-3" />
            <div className="font-display text-2xl mb-2">Snap your first receipt to light this up</div>
            <p className="opacity-70 text-sm mb-4 max-w-md mx-auto">
              As soon as you confirm a receipt we'll fill this dashboard with your spend by category, top stores,
              and everything expiring soon in your pantry.
            </p>
            <Link to="/food-pulse/receipts/new"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white"
                  style={{ background: GREEN }}>
              <Camera className="w-4 h-4" /> Snap first receipt
            </Link>
          </div>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 pb-16 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SpendTile data={data} />
          <ExpiringTile items={data.expiring_soon} count={data.expiring_count} pantryTotal={data.pantry_total} />
          <PointsTile points={data.contribution_points} />
          <VerticalTile items={data.this_month.by_vertical} className="lg:col-span-2" />
          <StoresTile items={data.this_month.top_stores} />
          <CategoriesTile items={data.this_month.top_categories} className="lg:col-span-3" />
        </div>
      )}
    </div>
  );
}

/* ================================================================
   Tiles
   ================================================================ */
function SpendTile({ data }) {
  const total = data.this_month?.total || 0;
  const delta = data.spend_delta_pct;
  return (
    <div className="rounded-2xl bg-white border p-5" style={{ borderColor: "#E9D8B4" }} data-testid="tile-spend">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-70 mb-1">
        <DollarSign className="w-3.5 h-3.5" /> Spend this month
      </div>
      <div className="font-display text-4xl font-bold" style={{ color: GREEN }}>{CURRENCY(total)}</div>
      <div className="mt-1 text-xs flex items-center gap-1">
        {delta == null ? (
          <span className="opacity-60">First month of data</span>
        ) : delta > 0 ? (
          <><TrendingUp className="w-3 h-3" style={{ color: RED }} /><span style={{ color: RED }}>{Math.abs(delta)}% higher</span><span className="opacity-60">vs last month</span></>
        ) : delta < 0 ? (
          <><TrendingDown className="w-3 h-3" style={{ color: GREEN }} /><span style={{ color: GREEN }}>{Math.abs(delta)}% lower</span><span className="opacity-60">vs last month</span></>
        ) : (<span className="opacity-60">Even with last month</span>)}
      </div>
      <div className="mt-3 text-[11px] opacity-60">
        {data.this_month?.receipt_count || 0} confirmed receipt{data.this_month?.receipt_count === 1 ? "" : "s"}
      </div>
    </div>
  );
}

function ExpiringTile({ items = [], count = 0, pantryTotal = 0 }) {
  return (
    <div className="rounded-2xl bg-white border p-5" style={{ borderColor: count > 0 ? RED : "#E9D8B4" }} data-testid="tile-expiring">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-70 mb-1">
        <Clock className="w-3.5 h-3.5" /> Expiring within 3 days
      </div>
      <div className="font-display text-4xl font-bold" style={{ color: count > 0 ? RED : GREEN }}>{count}</div>
      <div className="text-xs opacity-60 mt-0.5">
        of {pantryTotal} pantry item{pantryTotal === 1 ? "" : "s"}
      </div>
      {items.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {items.slice(0, 3).map((it) => (
            <li key={it.id} className="text-xs flex items-center gap-2">
              <AlertTriangle className="w-3 h-3" style={{ color: RED }} />
              <span className="truncate flex-1">{it.food_item_name}</span>
              <span className="opacity-60 flex-shrink-0">{it.expiry_date?.slice(5)}</span>
            </li>
          ))}
        </ul>
      )}
      <Link to="/food-pulse/my-lists?tab=pantry" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold underline">
        View pantry <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

function PointsTile({ points = 0 }) {
  return (
    <div className="rounded-2xl border p-5 flex flex-col justify-between"
         style={{ borderColor: AMBER, background: "linear-gradient(135deg,#FFEDD5,#FFF7E4)" }}
         data-testid="tile-points">
      <div>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-70 mb-1">
          <Award className="w-3.5 h-3.5" /> Contribution points
        </div>
        <div className="font-display text-4xl font-bold" style={{ color: GREEN }}>{points}</div>
        <div className="text-[11px] opacity-70 mt-0.5">Every confirmed receipt earns points</div>
      </div>
      <Link to="/food-pulse/receipts/mine" className="mt-3 text-xs font-semibold underline flex items-center gap-1">
        View activity <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

function VerticalTile({ items = [], className = "" }) {
  const total = items.reduce((s, x) => s + x.amount, 0) || 1;
  return (
    <div className={`rounded-2xl bg-white border p-5 ${className}`} style={{ borderColor: "#E9D8B4" }} data-testid="tile-vertical">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-70 mb-3">
        <Receipt className="w-3.5 h-3.5" /> Spend by category this month
      </div>
      {items.length === 0 ? (
        <div className="text-sm opacity-60">No receipts yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((v) => {
            const Ico = VERTICAL_ICON[v.key] || Receipt;
            const pct = Math.round((v.amount / total) * 100);
            return (
              <div key={v.key} data-testid={`vertical-row-${v.key}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="flex items-center gap-2"><Ico className="w-3.5 h-3.5 opacity-70" /> {v.label}</span>
                  <span className="font-semibold">{CURRENCY(v.amount)} <span className="opacity-60 text-xs">({pct}%)</span></span>
                </div>
                <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${pct}%`, background: AMBER }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StoresTile({ items = [] }) {
  return (
    <div className="rounded-2xl bg-white border p-5" style={{ borderColor: "#E9D8B4" }} data-testid="tile-stores">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-70 mb-3">
        <Store className="w-3.5 h-3.5" /> Top stores this month
      </div>
      {items.length === 0 ? (
        <div className="text-sm opacity-60">No receipts yet.</div>
      ) : (
        <ol className="space-y-2">
          {items.map((s, i) => (
            <li key={s.label} className="flex items-center gap-2 text-sm" data-testid={`store-row-${i}`}>
              <span className="opacity-40 text-xs w-4">{i + 1}.</span>
              <span className="flex-1 truncate">{s.label}</span>
              <span className="font-semibold text-xs">{CURRENCY(s.amount)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function CategoriesTile({ items = [], className = "" }) {
  const total = items.reduce((s, x) => s + x.amount, 0) || 1;
  return (
    <div className={`rounded-2xl bg-white border p-5 ${className}`} style={{ borderColor: "#E9D8B4" }} data-testid="tile-categories">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-70 mb-3">
        <Tag className="w-3.5 h-3.5" /> Top product categories bought
      </div>
      {items.length === 0 ? (
        <div className="text-sm opacity-60">Confirm more receipts to see your category mix.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          {items.map((c) => {
            const pct = Math.round((c.amount / total) * 100);
            return (
              <div key={c.label} className="rounded-xl p-3" style={{ background: "#FFF7E4" }} data-testid={`category-row-${c.label}`}>
                <div className="text-xs font-mono uppercase tracking-widest opacity-70 truncate">{c.label.replace(/-/g, " ")}</div>
                <div className="font-display text-2xl font-bold" style={{ color: GREEN }}>{CURRENCY(c.amount)}</div>
                <div className="text-[11px] opacity-60">{pct}% of matched spend</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
